import React, { useState, useEffect } from 'react';
import {
  Text,
  View,
  ActivityIndicator,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import Checkbox from 'expo-checkbox';

import LabeledTextInput from '../../../common/components/bohform/LabeledTextInput';
import { BasicModalContainer, ModalHeader, ModalBody, ModalFooter, commonModalStyle } from '../../../common/components/basicmodal';
import { createProductField, updateProductField } from '../../../api/Product';
import { useAlertModal } from '../../../common/hooks';
import { BOHButton } from '../../../common/components/bohtoolbar';
import { FIELD_TYPE_LABELS } from './InventoryFields';

interface FormValues {
  label: string;
  field_type: string;
  options: string;
  default_value: string;
  sort: string;
  active: boolean;
}

const InventoryFieldModal = ({
  isModalVisible,
  Field,
  setUpdateTrigger,
  closeModal,
}) => {
  const isUpdate = Boolean(Field);
  const { showAlert } = useAlertModal();
  const [isLoading, setIsLoading] = useState(false);
  const [validMessage, setValidMessage] = useState('');
  const [formValues, setFormValues] = useState<FormValues>({
    label: '',
    field_type: 'text',
    options: '',
    default_value: '',
    sort: '0',
    active: true,
  });

  useEffect(() => {
    if (isModalVisible) {
      let optionsText = '';
      if (Field?.options) {
        try {
          const parsed = JSON.parse(Field.options);
          if (Array.isArray(parsed)) optionsText = parsed.join(', ');
        } catch {
          optionsText = '';
        }
      }
      setFormValues({
        label: Field?.label || '',
        field_type: Field?.field_type || 'text',
        options: optionsText,
        default_value: Field?.default_value || '',
        sort: Field?.sort !== undefined && Field?.sort !== null ? String(Field.sort) : '0',
        active: Field ? Boolean(Field.active) : true,
      });
      setValidMessage('');
    }
  }, [isModalVisible, Field]);

  const changeFormValue = (name: keyof FormValues, value: any) => {
    setFormValues(prev => ({ ...prev, [name]: value }));
  };

  const validateInput = () => {
    if (!formValues.label.trim()) {
      setValidMessage('Label is required.');
      return false;
    }
    if (formValues.field_type === 'dropdown' && !formValues.options.trim()) {
      setValidMessage('Dropdown fields require at least one option (comma separated).');
      return false;
    }
    setValidMessage('');
    return true;
  };

  const handleResponse = (jsonRes: any, status: number) => {
    setIsLoading(false);
    if (status === 200 || status === 201) {
      showAlert('success', isUpdate ? 'Field updated' : 'Field created');
      setUpdateTrigger(true);
      closeModal();
    } else if (status === 409) {
      setValidMessage(jsonRes.error);
    } else {
      showAlert('error', jsonRes?.error || 'An unknown error occurred.');
    }
  };

  const submitHandler = async () => {
    if (!validateInput()) return;

    setIsLoading(true);
    const payload: any = {
      label: formValues.label.trim(),
      field_type: formValues.field_type,
      options: formValues.field_type === 'dropdown' ? formValues.options : null,
      default_value: formValues.default_value || null,
      sort: parseInt(formValues.sort, 10) || 0,
      active: formValues.active ? 1 : 0,
    };

    if (isUpdate) updateProductField(Field.id, payload, handleResponse);
    else createProductField(payload, handleResponse);
  };

  return isModalVisible ? (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer>
        <ModalHeader
          label={isUpdate ? 'Update Inventory Field' : 'Add Inventory Field'}
          closeModal={closeModal}
        />
        <ModalBody>
          <LabeledTextInput
            label='Label'
            placeholder="e.g. Serial Number"
            value={formValues.label}
            onChangeText={val => changeFormValue('label', val)}
            placeholderTextColor="#ccc"
          />
          <View style={{ marginVertical: 10 }}>
            <Text style={{ marginBottom: 4 }}>Type</Text>
            <Picker
              selectedValue={formValues.field_type}
              onValueChange={val => changeFormValue('field_type', val)}
            >
              {Object.keys(FIELD_TYPE_LABELS).map(t => (
                <Picker.Item key={t} label={FIELD_TYPE_LABELS[t]} value={t} />
              ))}
            </Picker>
          </View>
          {formValues.field_type === 'dropdown' && (
            <LabeledTextInput
              label='Options (comma separated)'
              placeholder="e.g. Small, Medium, Large"
              value={formValues.options}
              onChangeText={val => changeFormValue('options', val)}
              placeholderTextColor="#ccc"
            />
          )}
          <LabeledTextInput
            label='Default value'
            placeholder="Optional default"
            value={formValues.default_value}
            onChangeText={val => changeFormValue('default_value', val)}
            placeholderTextColor="#ccc"
          />
          <LabeledTextInput
            label='Sort order'
            placeholder="0"
            value={formValues.sort}
            onChangeText={val => changeFormValue('sort', val)}
            placeholderTextColor="#ccc"
          />
          <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 10 }}>
            <Checkbox
              value={formValues.active}
              onValueChange={val => changeFormValue('active', val)}
            />
            <Text style={{ marginLeft: 8 }}>Active</Text>
          </View>

          {validMessage ? (
            <Text style={{ color: 'red', marginTop: 10 }}>{validMessage}</Text>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <BOHButton
            label={isUpdate ? 'Update Field' : 'Add Field'}
            onPress={submitHandler}
            disabled={isLoading}
          >
            {isLoading ? <ActivityIndicator size="small" color="#fff" /> : (isUpdate ? 'Update Field' : 'Add Field')}
          </BOHButton>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
    </View>
  ) : null;
};

const styles = commonModalStyle;

export default InventoryFieldModal;
